import React from 'react';
import { PropTypes } from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import {
  Alert,
  IconButton,
  Stack,
  Link,
  Fade,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { clearZipState } from 'app/features/file_player/filePlayerSlice';

const DownloadLink = ({ url, onClick }) => {
  const handleClick = (e) => {
    e.preventDefault();
    window.open(url, '_blank');
    onClick();
  };

  return (
    <Link
      href={url}
      underline="none"
      onClick={handleClick}
      sx={{
        ml: 2,
        px: 2,
        py: 0.5,
        borderRadius: '4px',
        border: '1px solid',
        borderColor: 'primary.main',
        color: 'primary.main',
        fontWeight: 600,
        textTransform: 'none',
        '&:hover': {
          backgroundColor: 'rgba(25, 118, 210, 0.04)',
        },
      }}
    >
      Download
    </Link>
  );
};

const CloseAlertButton = ({ onClick }) => (
  <IconButton
    aria-label="close"
    color="inherit"
    size="small"
    onClick={onClick}
  >
    <CloseIcon fontSize="inherit" />
  </IconButton>
);

export const FileZipStatusNotice = () => {
  const dispatch = useDispatch();
  const { zipStatus, zipUrl, zipDismissed } = useSelector((state) => state.filePlayer);

  const statusMap = {
    building: {
      severity: 'info',
      message: (
        <>
          Preparing your ZIP file.
          <br />
          Stay logged in to your NiteFlirt account so that you can download it – you won’t be able to download it later.
        </>
      ),
    },
    ready: {
      severity: 'success',
      message: 'Your ZIP file is ready!',
      extra: (
        <DownloadLink
          url={zipUrl}
          onClick={() => dispatch(clearZipState())}
        />
      ),
    },
    failed: {
      severity: 'error',
      message: 'Failed to generate ZIP file. Please try again later.',
    },
  };

  const statusProps = statusMap[zipStatus];
  if (!statusProps || zipDismissed) return null;

  return (
    <Fade in timeout={1500} unmountOnExit>
      <Alert
        severity={statusProps.severity}
        sx={{ mt: 2 }}
        action={<CloseAlertButton onClick={() => dispatch(clearZipState())} />}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          {typeof statusProps.message === 'string' ? <span>{statusProps.message}</span> : statusProps.message}
          {statusProps.extra}
        </Stack>
      </Alert>
    </Fade>
  );
};

DownloadLink.propTypes = {
  url: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
};

CloseAlertButton.propTypes = {
  onClick: PropTypes.func.isRequired,
};
